
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import Toast from '@/utils/toast';
import FullPageSpinner from '@/components/shared/suites/FullPageSpinner.vue';
import RotationalMenuItemList from './RotationalMenuItemList.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'menus';

@Component<RotationalMenuItemModal>({
	components: {
		TextField,
		FullPageSpinner,
		RotationalMenuItemList,
		StandardModal,
		WarningModal
	}
})
export default class RotationalMenuItemModal extends Vue {
	@Action('createRotationalMenu', { namespace }) private createRotationalMenu!: (payload: RotationalMenuPayload) => Promise<void>;
	@Action('updateRotationalMenu', { namespace }) private updateRotationalMenu!: (payload: RotationalMenuPayload) => Promise<void>;
	@Getter('getMenus', { namespace }) private menus!: Menu[];
	@Prop({ type: Object, required: true }) private rotationalMenu!: RotationalMenu;
	@Prop({ type: String, required: true }) private startDate!: string;
	@Prop({ type: String, required: true }) private endDate!: string;

	private selectedItems: RotationalMenuPayloadItem[] = [];
	private initialSelectedItems: RotationalMenuPayloadItem[] = [];
	private expandedMenuIds: number[] = [];
	private searchString: string = '';
	private savingItems: boolean = false;
	private showWarningModal: boolean = false;

	/**
	 * Format menus property from rotational menu object by flattening the sections and adding menu_id to each section item
	 * Then filter out any menus that have no items
	 *
	 * @return {Menu[]}
	 */
	private get formattedMenus(): Menu[] {
		return this.menus.map((menu: Menu) => {
			return {
				...menu,
				items: menu.sections?.length
					? menu.sections.flatMap((section: MenuSection) => {
						const items = section.items?.map((item: MenuItem) => {
							return {
								...item,
								menu_id: menu.id
							};
						}) || [];
						return items.filter((item): item is MenuItem => item !== undefined);
					})
					: menu.items
			};
		}).filter((menu: Menu) => menu.items?.length);
	}

	private get dateString(): string {
		return DateTime.fromISO(this.rotationalMenu?.date).setLocale(this.$i18n.locale).toFormat('DDD (cccc)');
	}

	private created(): void {
		this.formatSelectedItems();
	}

	/**
	 * Get selected items from the rotational menu prop and format them to match expected payload
	 *
	 * @return {void}
	 */
	private formatSelectedItems(): void {
		if (!this.rotationalMenu?.menus.length) {
			this.selectedItems = [];
		}

		this.selectedItems = this.rotationalMenu.menus.flatMap((menu: Menu) => {
			if (menu.sections?.length) {
				return menu.sections.flatMap((section: MenuSection) => {
					return (section.items?.map((item: MenuItem) => {
						return {
							rotational_menu_id: this.rotationalMenu?.id || null,
							menu_id: menu.id,
							section_item_id: item.section_id ? item.id : null,
							menu_item_id: item.section_id ? null : item.id,
							section_id: item.section_id || null
						} as RotationalMenuPayloadItem;
					}) ?? []);
				});
			}
			if (menu.items?.length) {
				return menu.items?.map((item: MenuItem) => {
					return {
						rotational_menu_id: this.rotationalMenu?.id || null,
						menu_id: menu.id,
						section_item_id: item.section_id ? item.id : null,
						menu_item_id: item.section_id ? null : item.id,
						section_id: item.section_id || null
					} as RotationalMenuPayloadItem;
				});
			}
			return [];
		});

		this.initialSelectedItems = cloneDeep(this.selectedItems);
	}

	/**
	 * Format items that were selected in the RotationalMenuListItem component, then add them to selected items array
	 * If the item is already selected, remove it from the array
	 *
	 * @param {MenuItem[]} items
	 */
	private updateSelectedItems(items: MenuItem[]): void {
		items.forEach((item: MenuItem) => {
			// Menu id needs to be checked as well as item id because MenuItems and SectionItems can have the same id
			const selectedItemIndex = this.selectedItems.findIndex((selectedItem: RotationalMenuPayloadItem) => selectedItem.menu_id === item.menu_id && (selectedItem.section_item_id === item.id || selectedItem.menu_item_id === item.id));
			if (selectedItemIndex === -1) {
				this.selectedItems.push({
					rotational_menu_id: this.rotationalMenu?.id || null,
					menu_id: item.menu_id,
					section_item_id: item.section_id ? item.id : null,
					menu_item_id: item.section_id ? null : item.id,
					section_id: item.section_id || null
				});
			}
			else if (selectedItemIndex !== -1) {
				this.selectedItems.splice(selectedItemIndex, 1);
			}
		});
	}

	/**
	 * Remove all items for a specific menu
	 *
	 * @param {number} menuId
	 */
	private removeAllItemsForMenu(menuId: number): void {
		this.selectedItems = this.selectedItems.filter((selectedItem: RotationalMenuPayloadItem) => selectedItem.menu_id !== menuId);
	}

	/**
	 * Save the rotational menu with the selected items
	 * If the rotational menu id is defined the menu already exists, so update it
	 * Otherwise create a new rotatational menu
	 *
	 * @return {Promise<void>}
	 */
	private async saveRotationalMenu(): Promise<void> {
		this.savingItems = true;
		const payload: RotationalMenuPayload = {
			id: this.rotationalMenu.id,
			date: this.rotationalMenu.date,
			items: this.selectedItems
		};
		try {
			this.rotationalMenu.id
				? await this.updateRotationalMenu(payload)
				: await this.createRotationalMenu(payload);

			this.$emit('rotational-menu-changed');
		}
		catch (errorMessage) {
			Toast.error(this.rotationalMenu.menus.length
				? this.$t('menu.editor.availability.rotational_menus.error_updating_menu_items', { errorMessage })
				: this.$t('menu.editor.availability.rotational_menus.error_adding_menu_items', { errorMessage }));
		}
		finally {
			this.savingItems = false;
		}
	}

	/**
	 * Check if selected items have changed, if they have show warning modal
	 * Otherwise close the modal
	 *
	 * @return {void}
	 */
	private handleClose(): void {
		const selectedItemsChanged = !isEqual(sortBy(this.selectedItems, ['menu_id', 'section_item_id', 'menu_item_id']), sortBy(this.initialSelectedItems, ['menu_id', 'section_item_id', 'menu_item_id']));
		selectedItemsChanged ? this.showWarningModal = true : this.$emit('close');
	}
}
